import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { MovementDrawerProvider } from "@/shared/contexts/MovementDrawerContext";
import { toastErrorIfNotForbidden } from "@/shared/utils/errorHandling";
import { accountingService } from "../services/accounting.service";
import MovementAllocationsDrawer from "./MovementAllocationsDrawer";
import type { BankMovement } from "../types/accounting.types";

type Props = { children: React.ReactNode };

/**
 * Provides the movement drawer globally so any feature can open it
 * without navigating to the accounting page.
 */
export const MovementDrawerProviderImpl: React.FC<Props> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bankMovement, setBankMovement] = useState<BankMovement | null>(null);

  const openMovement = useCallback(async (movementId: string) => {
    try {
      const m = await accountingService.getBankMovement(movementId);
      if (!m) {
        toast.warning("Mouvement introuvable");
        return;
      }
      setBankMovement(m);
      setDrawerOpen(true);
    }
    catch (err) {
      toastErrorIfNotForbidden(err, "Impossible de charger le mouvement");
    }
  }, []);

  const closeMovement = useCallback(() => {
    setDrawerOpen(false);
    setBankMovement(null);
  }, []);

  const ctx = useMemo(() => ({ openMovement, closeMovement }), [openMovement, closeMovement]);

  return (
    <MovementDrawerProvider value={ctx}>
      {children}
      {(drawerOpen || bankMovement) && (
        <MovementAllocationsDrawer
          open={drawerOpen}
          onOpenChange={(open) => {
            if (!open) closeMovement();
          }}
          bankMovement={bankMovement}
          onBankMovementUpdated={(updated) => setBankMovement(updated)}
          onBankMovementDeleted={closeMovement}
          onBack={closeMovement}
        />
      )}
    </MovementDrawerProvider>
  );
};
